import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { emailingAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Mail,
  Send,
  Eye,
  Pencil,
  Trash2,
  Copy,
  Loader2,
  Clock,
  CheckCircle,
  Users,
  Sparkles,
  BarChart3,
  PlayCircle,
  Calendar,
  Filter,
} from 'lucide-react';

const CAMPAIGN_TYPE_LABELS = {
  relance_devis: { label: 'Relance devis', color: 'bg-orange-100 text-orange-700' },
  relance_inactifs: { label: 'Relance inactifs', color: 'bg-yellow-100 text-yellow-700' },
  bienvenue: { label: 'Bienvenue', color: 'bg-green-100 text-green-700' },
  newsletter: { label: 'Newsletter', color: 'bg-blue-100 text-blue-700' },
  promotion: { label: 'Promotion', color: 'bg-purple-100 text-purple-700' },
  custom: { label: 'Personnalisé', color: 'bg-slate-100 text-slate-700' },
};

const EmailingPage = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [total, setTotal] = useState(0);
  
  // Create campaign dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    campaign_type: 'relance_devis'
  });
  
  // Stats dialog
  const [statsOpen, setStatsOpen] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, [statusFilter]);

  const fetchCampaigns = async () => {
    try {
      const status = statusFilter === 'all' ? null : statusFilter;
      const response = await emailingAPI.getCampaigns(status);
      setCampaigns(response.data.campaigns);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Erreur lors du chargement des campagnes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCampaign.name.trim()) {
      toast.error('Veuillez entrer un nom de campagne');
      return;
    }
    
    setCreateLoading(true);
    try {
      const response = await emailingAPI.createCampaign(newCampaign);
      toast.success('Campagne créée !');
      setCreateOpen(false);
      setNewCampaign({ name: '', campaign_type: 'relance_devis' });
      // Naviguer vers l'éditeur
      navigate(`/admin/emailing/${response.data.id}`);
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDuplicate = async (campaignId) => {
    try {
      const response = await emailingAPI.duplicateCampaign(campaignId);
      toast.success('Campagne dupliquée !');
      fetchCampaigns();
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleDelete = async (campaignId) => {
    if (!window.confirm('Supprimer cette campagne ?')) return;
    
    try {
      await emailingAPI.deleteCampaign(campaignId);
      toast.success('Campagne supprimée');
      fetchCampaigns();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleShowStats = async (campaign) => {
    setStatsLoading(true);
    setStatsOpen(true);
    setStatsData({ campaign });
    
    try {
      const response = await emailingAPI.getCampaignStats(campaign.id);
      setStatsData({ campaign, stats: response.data });
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setStatsLoading(false);
    }
  };

  const filteredCampaigns = campaigns.filter((c) =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.subject?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Envoyée</Badge>;
      case 'scheduled':
        return <Badge className="bg-blue-100 text-blue-700"><Clock className="w-3 h-3 mr-1" />Planifiée</Badge>;
      case 'draft':
      default:
        return <Badge className="bg-slate-100 text-slate-700"><Pencil className="w-3 h-3 mr-1" />Brouillon</Badge>;
    }
  };

  const getCampaignTypeBadge = (type) => {
    const config = CAMPAIGN_TYPE_LABELS[type] || CAMPAIGN_TYPE_LABELS.custom;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="emailing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            Emailing IA
          </h1>
          <p className="text-slate-500 mt-1">{total} campagnes • Automatisation & génération IA</p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/emailing/automation')}
            data-testid="automation-rules-btn"
          >
            <Clock className="w-4 h-4 mr-2" />
            Automatisations
          </Button>
          <Button
            className="bg-orange-500 hover:bg-orange-600"
            onClick={() => setCreateOpen(true)}
            data-testid="new-campaign-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle campagne
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{total}</p>
              <p className="text-sm text-slate-500">Campagnes</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Send className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {campaigns.filter(c => c.status === 'sent').length}
              </p>
              <p className="text-sm text-slate-500">Envoyées</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {campaigns.filter(c => c.status === 'scheduled').length}
              </p>
              <p className="text-sm text-slate-500">Planifiées</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Sparkles className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {campaigns.reduce((acc, c) => acc + (c.sent_count || 0), 0)}
              </p>
              <p className="text-sm text-slate-500">Emails envoyés</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher une campagne..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-campaigns-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="draft">Brouillons</SelectItem>
                <SelectItem value="scheduled">Planifiées</SelectItem>
                <SelectItem value="sent">Envoyées</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Campaigns Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {filteredCampaigns.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucune campagne pour le moment</p>
              <p className="text-sm mt-2">Créez votre première campagne d'emailing</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Campagne</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Type</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Statut</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 hidden md:table-cell">Envois</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Date</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCampaigns.map((campaign) => (
                    <TableRow key={campaign.id} data-testid={`campaign-row-${campaign.id}`}>
                      <TableCell>
                        <div className="max-w-md">
                          <p className="font-medium text-slate-800 truncate">{campaign.name}</p>
                          {campaign.subject && (
                            <p className="text-sm text-slate-500 truncate">📧 {campaign.subject}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getCampaignTypeBadge(campaign.campaign_type)}</TableCell>
                      <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Users className="w-4 h-4" />
                          {campaign.sent_count || 0}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500 hidden lg:table-cell">
                        {formatDate(campaign.updated_at || campaign.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => navigate(`/admin/emailing/${campaign.id}`)}
                            title="Éditer"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleShowStats(campaign)}
                            title="Statistiques"
                          >
                            <BarChart3 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleDuplicate(campaign.id)}
                            title="Dupliquer"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500"
                            onClick={() => handleDelete(campaign.id)}
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Campaign Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              <Mail className="w-5 h-5 inline mr-2 text-orange-500" />
              Nouvelle campagne
            </DialogTitle>
            <DialogDescription>
              Créez une nouvelle campagne d'emailing
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom de la campagne *</Label>
              <Input
                id="name"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                placeholder="Ex: Relance devis mars 2026"
                required
                data-testid="campaign-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Type de campagne</Label>
              <Select
                value={newCampaign.campaign_type}
                onValueChange={(value) => setNewCampaign({ ...newCampaign, campaign_type: value })}
              >
                <SelectTrigger data-testid="campaign-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="relance_devis">📋 Relance devis non validés</SelectItem>
                  <SelectItem value="relance_inactifs">💤 Relance clients inactifs</SelectItem>
                  <SelectItem value="bienvenue">👋 Email de bienvenue</SelectItem>
                  <SelectItem value="newsletter">📰 Newsletter</SelectItem>
                  <SelectItem value="promotion">🎁 Offre promotionnelle</SelectItem>
                  <SelectItem value="custom">✏️ Campagne personnalisée</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-orange-500 hover:bg-orange-600"
                disabled={createLoading}
                data-testid="create-campaign-btn"
              >
                {createLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Créer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stats Dialog */}
      <Dialog open={statsOpen} onOpenChange={setStatsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              <BarChart3 className="w-5 h-5 inline mr-2" />
              Statistiques
            </DialogTitle>
          </DialogHeader>
          {statsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : statsData && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <Card className="border-slate-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-slate-800">
                      {statsData.stats?.sent_count || statsData.campaign?.sent_count || 0}
                    </p>
                    <p className="text-sm text-slate-500">Emails envoyés</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-slate-800">
                      {statsData.stats?.send_history?.length || 0}
                    </p>
                    <p className="text-sm text-slate-500">Envois effectués</p>
                  </CardContent>
                </Card>
              </div>
              
              {statsData.stats?.last_sent_at && (
                <p className="text-sm text-slate-500">
                  Dernier envoi : {formatDate(statsData.stats.last_sent_at)}
                </p>
              )}
              
              {statsData.stats?.send_history?.length > 0 && (
                <div className="mt-4">
                  <p className="font-medium text-slate-700 mb-2">Historique des envois</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {statsData.stats.send_history.slice().reverse().map((h, idx) => (
                      <div key={idx} className="p-2 bg-slate-50 rounded text-sm">
                        <div className="flex justify-between">
                          <span>{formatDate(h.date)}</span>
                          <span className="font-medium">{h.sent_count}/{h.recipients_count} envoyés</span>
                        </div>
                        {h.test_mode && <Badge variant="outline" className="mt-1">Test</Badge>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailingPage;
